import { z } from 'zod'
import { calendarDay, calendarDaySchema, type CalendarDay } from '../validators/calendar-day.validator.js'
import { floorObjectSchema } from '../dto/floor-object.dto.js'
import { primitiveSchema } from '../dto/primitive.dto.js'
import { coverImageSchema } from './cover-image.input.js'
import { phaseTypeSchema } from '../enums/phase-type.enum.js'
import { furnitureClassSchema } from '../enums/furniture-class.enum.js'
import { danceFloorFeetSchema, danceFloorUpdateIssue, settleDanceFloor } from '../helpers/dance-floor.js'
import { riserFeetSchema, riserUpdateIssue, settleRiser } from '../helpers/riser.js'
import { furnitureImageSchema, furnitureMaxCapacitySchema } from './furniture-item.input.js'
import { emailSchema } from '../validators/email.validator.js'
import { phoneSchema } from '../validators/phone.validator.js'
import { staffCancellationReasonSchema } from '../enums/cancellation-reason.enum.js'
import { lostReasonSchema } from '../enums/lost-reason.enum.js'
import { SETUP_INSTRUCTIONS_MAX } from '../constants/public-schedule.js'
import { GHL_MAX_LINKS_PER_EVENT } from '../constants/gohighlevel.js'
import { ghlOpportunityRefSchema } from './gohighlevel.input.js'

/** The years an event can be on: wide enough for any real booking or import. */
export const EVENT_YEARS = { min: 2000, max: 2099 } as const

/** The first and last days {@link isPlausibleEventDay} will accept. */
export const MIN_EVENT_DAY: CalendarDay = calendarDay(`${String(EVENT_YEARS.min)}-01-01`)
export const MAX_EVENT_DAY: CalendarDay = calendarDay(`${String(EVENT_YEARS.max)}-12-31`)

/** The longest staff notes an event can have. */
export const EVENT_NOTES_MAX = 5000

/** Venue staff's notes: never shown to the event's clients, planner or vendors. Empty means none. */
const eventNotesSchema = z.string().trim().max(EVENT_NOTES_MAX, `Notes can be at most ${String(EVENT_NOTES_MAX)} characters.`)

/**
 * Whether a day is one an event could plausibly be on.
 *
 * A date field that saves as it changes sends every intermediate value while a
 * year is typed — 0002, 0020, 0202, then 2027 — and each one used to be a real
 * save: the event, its plans and its timeline moved to the year 2 and back. The
 * web holds a date until it is plausible; the API refuses one that is not.
 */
export function isPlausibleEventDay(day: CalendarDay): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return false
  const year = Number(day.slice(0, 4))
  return year >= EVENT_YEARS.min && year <= EVENT_YEARS.max
}

const eventDaySchema = calendarDaySchema.refine(
  isPlausibleEventDay,
  `An event date must be between ${String(EVENT_YEARS.min)} and ${String(EVENT_YEARS.max)}.`,
)

/** An existing client contact, by id. */
export const existingEventClientInputSchema = z.object({ clientId: z.string().uuid() }).strict()

/** A new client contact, found-or-created in the tenant by email. */
export const newEventClientInputSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    email: emailSchema,
    /** Kept only for someone new; an existing client's phone is changed on its own. */
    phone: phoneSchema.nullish(),
  })
  .strict()

/**
 * Add a client contact to an event: an existing client by `clientId`, or a new
 * one by `name` + `email`. Exactly one, which is why this is a union of the two
 * shapes rather than three optional fields guarded by a refine.
 *
 * The refine said the same thing to a caller and nothing at all to the compiler:
 * `z.infer` gave every field as optional, so the service that consumes this had
 * to assert `input.email as string` on a guarantee only the schema knew about. A
 * union puts the guarantee in the type — `'clientId' in input` narrows, and the
 * assertions are gone.
 *
 * Validating one branch (a form that only ever adds a new contact) is what
 * `newEventClientInputSchema` is for: a union reports "no branch matched", while
 * the member schema reports which field is wrong.
 */
export const addEventClientInputSchema = z.union(
  [existingEventClientInputSchema, newEventClientInputSchema],
  { error: 'Provide an existing clientId, or a name and email for a new contact.' },
)

export type AddEventClientInput = z.infer<typeof addEventClientInputSchema>

/**
 * Someone an import found on an event — a Social Tables collaborator — to put
 * on it as what they are: its planner when their address belongs to a planning
 * company, a client otherwise (docs/planner-vendors.md, slice 6a).
 */
export const addEventCollaboratorInputSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    /** The address a client is stored under — redirected outside production. */
    email: emailSchema,
    /** The address as Social Tables gave it, which a planner's account holds in production. */
    sourceEmail: emailSchema,
    /**
     * Whether a planning company's person may make it the planner: only on an
     * event this import is creating. On one that exists, and on a re-import from
     * scratch (whose planner comes across from the event it replaces), what the
     * venue did is the answer — a planner it took off stays off.
     */
    mayAssignPlanner: z.boolean(),
  })
  .strict()

export type AddEventCollaboratorInput = z.infer<typeof addEventCollaboratorInputSchema>

/**
 * The two clocks must differ. Which day the end falls on is not the caller's
 * problem.
 *
 * This used to require the end *timestamp* to be strictly later, which made a
 * 22:00–01:00 reception unrepresentable: a client naturally sends both ends on
 * the event's date, and that was refused. The doc's plan was for every client to
 * roll the end date forward itself, but a rule every caller must know is a rule
 * some caller will not — so the API takes the two clocks and rolls the end
 * itself (`eventWindow`), and this only checks the thing that is genuinely
 * wrong.
 *
 * **Equal clocks are that thing.** 22:00 to 22:00 is either nothing or a full
 * day, and neither is what anybody meant.
 *
 * The stored window still ends strictly after it starts — the roll guarantees
 * it — so nothing downstream has to reconstruct anything.
 */
function clocksDiffer(v: { startsAt?: string | null | undefined; endsAt?: string | null | undefined }): boolean {
  if (!v.startsAt || !v.endsAt) return true
  return v.startsAt.slice(11, 16) !== v.endsAt.slice(11, 16)
}
const timeWindowRefine = {
  message: 'An event must start and end at different times',
  path: ['endsAt'],
}

/** The longest an event's name may be. The form says so before it is reached. */
export const EVENT_NAME_MAX = 160

/**
 * An event's name. The messages are what a person reads under the field: the
 * defaults ("Too big: expected string to have <=160 characters") reached the
 * New event form word for word.
 */
const eventNameSchema = z
  .string()
  .trim()
  .min(1, 'Name is required')
  .max(EVENT_NAME_MAX, `Keep the name to ${String(EVENT_NAME_MAX)} characters or fewer.`)

/** A guest count, worded for the person typing it ("Too small: expected number to be >=0" was not). */
const guestCountSchema = z
  .number({ error: 'Enter the guest count as a number.' })
  .int('Enter the guest count as a whole number.')
  .nonnegative('The guest count can’t be below 0.')

/** Create an event at a venue, optionally dated and tied to a client. `startsAt`
 * / `endsAt` are the event's time window on `date`. */
export const createEventInputSchema = z
  .object({
    name: eventNameSchema,
    venueId: z.string().uuid(),
    /**
     * The tracks to book. Empty or absent means venue-wide.
     *
     * A set because an event may occupy several, each checked for a clash in
     * its window when the event is booked; see `docs/event-scheduling.md`.
     *
     * The single-valued `eventTrackId` this used to accept beside it is gone,
     * with the column it mirrored.
     */
    trackIds: z.array(z.string().uuid()).optional(),
    date: eventDaySchema.nullish(),
    startsAt: z.string().datetime().nullish(),
    endsAt: z.string().datetime().nullish(),
    clientIds: z.array(z.string().uuid()).optional(),
    guestCount: guestCountSchema.optional(),
    notes: eventNotesSchema.nullish(),
    /** The couple's GoHighLevel opportunities, picked on the form: the event is linked to them as it is made. */
    ghlLinks: z.array(ghlOpportunityRefSchema).max(GHL_MAX_LINKS_PER_EVENT).optional(),
    /**
     * Start the event with a phase for every space on its track, which is the
     * default and what someone creating an event by hand wants: the rooms are
     * bookable, so they are there to plan.
     *
     * An import passes false. It knows which rooms the event actually uses, and
     * seeding the rest left an imported event showing five plans where the source
     * had one — four of them empty.
     */
    seedPhases: z.boolean().optional(),
    /**
     * The Social Tables event this was imported from. Only the importer sends it,
     * and it is what the Prism catalog matches on — a renamed event used to import
     * a second time, because the only link was the name.
     */
    prismEventUid: z.string().trim().min(1).max(200).nullish(),
  })
  .strict()
  .refine(clocksDiffer, timeWindowRefine)

export type CreateEventInput = z.infer<typeof createEventInputSchema>

/** Edit an event's own fields — name, date, time window, guest count, notes and/or a HoneyBook booking's value. */
export const updateEventInputSchema = z
  .object({
    name: eventNameSchema.optional(),
    date: eventDaySchema.nullish(),
    startsAt: z.string().datetime().nullish(),
    endsAt: z.string().datetime().nullish(),
    guestCount: guestCountSchema.optional(),
    /** Null or empty clears them. Omit to leave them unchanged. */
    notes: eventNotesSchema.nullable().optional(),
    // Omit to leave unchanged, null to remove the cover, or a new image. It
    // rides on this input because changing an event's photograph needs the same
    // permission as changing anything else about the event.
    coverImageUrl: coverImageSchema.nullable().optional(),
    /** Set by the importer when it adopts an event it matched by name. */
    prismEventUid: z.string().trim().min(1).max(200).nullish(),
    /**
     * The tracks the event takes, replacing the ones it has. The same rules as
     * on creation — a venue that runs concurrent events needs at least one —
     * and refused if the event already has plans in a room the new set leaves
     * out. Omit to leave them unchanged.
     */
    trackIds: z.array(z.string().uuid()).optional(),
    /**
     * What the couple booked, in cents, on an event made from a HoneyBook
     * booking; null clears it. Refused on any other event, whose value comes
     * from Sales.
     */
    bookedValueCents: z.number().int().min(0, 'A booked value can’t be negative.').max(100_000_000, 'That booked value is too high.').nullable().optional(),
  })
  .strict()
  .refine(clocksDiffer, timeWindowRefine)

export type UpdateEventInput = z.infer<typeof updateEventInputSchema>

/**
 * Set an event's guest count: the one detail of the event its couple and their
 * planner may change as well as its staff (docs/planner-vendors.md, "Who can do
 * what"). The same rule as `guestCount` above, on its own so their route can
 * take nothing else.
 */
export const setGuestCountInputSchema = z
  .object({
    guestCount: guestCountSchema,
  })
  .strict()

export type SetGuestCountInput = z.infer<typeof setGuestCountInputSchema>

/** Save an event phase's furniture map. */
export const savePhaseInputSchema = z
  .object({
    objects: z.array(floorObjectSchema),
    /**
     * Sent by the Prism import. The save is then refused (409) if the plan has
     * been changed in CSE since the import last wrote it, or is open in the
     * editor right now — a re-import brings the source's plan in, it does not
     * erase somebody's arrangement.
     */
    fromImport: z.boolean().optional(),
    /**
     * With `fromImport`: also replace a plan imported before CSE kept track of
     * what the import wrote, even though it no longer matches the source. The
     * job's `replaceUntrackedPlans`; see there for when that is wanted.
     */
    replaceUntracked: z.boolean().optional(),
    /**
     * The editor emptied the plan on purpose. A save that would leave a plan
     * with pieces on it empty is refused without this — that is what a stale
     * client or a sync bug sends.
     */
    clear: z.boolean().optional(),
  })
  .strict()

export type SavePhaseInput = z.infer<typeof savePhaseInputSchema>

/** The longest a phase's name may be; the name fields stop typing there. */
export const PHASE_LABEL_MAX = 120

const phaseLabelSchema = z
  .string()
  .trim()
  .min(1, 'Give the phase a name.')
  .max(PHASE_LABEL_MAX, `Keep the phase name to ${String(PHASE_LABEL_MAX)} characters or fewer.`)

/**
 * Add a phase to an event: a segment in one of the venue's (track's) spaces,
 * optionally timed and typed. Its plan starts as a copy of another phase's
 * (`copyFromPhaseId`), empty (`blank`), or otherwise as the room's default
 * layout, which is empty in a room without one.
 */
export const createPhaseInputSchema = z
  .object({
    label: phaseLabelSchema,
    venueSpaceId: z.string().uuid(),
    type: phaseTypeSchema.optional(),
    startsAt: z.string().datetime().nullish(),
    endsAt: z.string().datetime().nullish(),
    copyFromPhaseId: z.string().uuid().nullish(),
    /**
     * Start with nothing on the plan, even in a room with a default layout.
     *
     * Without it a new phase takes the room's default layout, and the editor's
     * "Blank layout" used to send nothing more: in a room with a default it
     * arrived furnished.
     */
    blank: z.boolean().optional(),
  })
  .strict()
  .refine((input) => !(input.blank === true && input.copyFromPhaseId), {
    message: 'A phase can start blank or as a copy, not both',
    path: ['blank'],
  })

export type CreatePhaseInput = z.infer<typeof createPhaseInputSchema>

/** Replace an event's brought-in (rented) furniture with the given lines (full desired state). */
/**
 * Add a client-brought furniture item to an event ("Add your own"): a custom
 * piece designed on the spot (name, class, shape, optional photo) plus how many
 * are brought in. A dance floor is not drawn; it names a default size and the
 * step it can grow by. Nor is a riser; it names one unit's width and length.
 * The item is scoped to this event, not the shared catalog.
 */
export const addBroughtInInputSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required').max(120),
    class: furnitureClassSchema.default('Other'),
    primitives: z.array(primitiveSchema).default([]),
    imageUrl: furnitureImageSchema.nullish(),
    quantity: z.number().int().positive('Count must be at least 1'),
    /**
     * Seats at a table somebody is bringing in. Kept apart from `quantity` for
     * the reason the catalog schema gives: one is how many of the piece arrive,
     * the other how many people one of them seats. Without it a client's own
     * table sat on the plan contributing nothing to the seat total, which made
     * the total wrong rather than incomplete.
     */
    maxCapacity: furnitureMaxCapacitySchema.nullish(),
    /** How big this dance floor starts, in feet. Required with the increment when the class is DanceFloor. */
    danceFloorDefaultFeet: danceFloorFeetSchema.nullish(),
    /** The step, in feet, by which this dance floor may grow once it is on the plan. */
    danceFloorIncrementFeet: danceFloorFeetSchema.nullish(),
    /** How wide one unit of this riser is, in feet. Required with its length when the class is Riser. */
    riserUnitWidthFeet: riserFeetSchema.nullish(),
    /** How long one unit of this riser is, in feet. */
    riserUnitLengthFeet: riserFeetSchema.nullish(),
  })
  .strict()
  .superRefine((value, ctx) => {
    const settled = settleDanceFloor(value.class, value.danceFloorDefaultFeet ?? null, value.danceFloorIncrementFeet ?? null)
    if (!settled.ok) ctx.addIssue({ code: 'custom', message: settled.message, path: ['danceFloorDefaultFeet'] })
    const riser = settleRiser(value.class, value.riserUnitWidthFeet ?? null, value.riserUnitLengthFeet ?? null)
    if (!riser.ok) ctx.addIssue({ code: 'custom', message: riser.message, path: ['riserUnitWidthFeet'] })
  })

export type AddBroughtInInput = z.infer<typeof addBroughtInInputSchema>

/**
 * Change a brought-in piece itself — what it is called, what it is, what it
 * looks like, what it seats, and how many arrived.
 *
 * Separate from the count-only route, which keeps its "0 removes it" meaning.
 * An edit has no business deleting the piece: that is the remove control, and a
 * count typed down to nothing mid-edit should be a validation error rather than
 * a silent deletion of the thing being edited.
 */
export const updateBroughtInInputSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required').max(120).optional(),
    class: furnitureClassSchema.optional(),
    primitives: z.array(primitiveSchema).optional(),
    imageUrl: furnitureImageSchema.nullable().optional(),
    /** Omit to leave unchanged, null to clear the seat count. */
    maxCapacity: furnitureMaxCapacitySchema.nullable().optional(),
    /** Omit to leave the stored size alone. Clearing either one is refused while the piece is a dance floor. */
    danceFloorDefaultFeet: danceFloorFeetSchema.nullable().optional(),
    danceFloorIncrementFeet: danceFloorFeetSchema.nullable().optional(),
    /** Omit to leave the stored unit alone. Clearing either one is refused while the piece is a riser. */
    riserUnitWidthFeet: riserFeetSchema.nullable().optional(),
    riserUnitLengthFeet: riserFeetSchema.nullable().optional(),
    quantity: z.number().int().positive('Count must be at least 1').optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    const message = danceFloorUpdateIssue(value.class, value.danceFloorDefaultFeet, value.danceFloorIncrementFeet)
    if (message !== null) ctx.addIssue({ code: 'custom', message, path: ['danceFloorDefaultFeet'] })
    const riser = riserUpdateIssue(value.class, value.riserUnitWidthFeet, value.riserUnitLengthFeet)
    if (riser !== null) ctx.addIssue({ code: 'custom', message: riser, path: ['riserUnitWidthFeet'] })
  })

export type UpdateBroughtInInput = z.infer<typeof updateBroughtInInputSchema>

/** Change how many of a brought-in item are on the plan (0 removes it). */
/**
 * What taking a piece off an event does to the confirmed and finished plans
 * that have it placed. `keep` (the default) leaves them as they were settled;
 * `remove` takes the piece off them too. Open plans always keep the pieces,
 * marked as removed, until somebody takes them off.
 */
export const confirmedPlansChoiceSchema = z.enum(['keep', 'remove'])
export type ConfirmedPlansChoice = z.infer<typeof confirmedPlansChoiceSchema>

export const setBroughtInQuantityInputSchema = z
  .object({ quantity: z.number().int().nonnegative(), confirmedPlans: confirmedPlansChoiceSchema.optional() })
  .strict()

export type SetBroughtInQuantityInput = z.infer<typeof setBroughtInQuantityInputSchema>

/** Select how many of a gallery piece this event uses (venue-owned or vendor),
 * from the gallery (0 removes the selection). The item comes from the route. */
export const setRentalInputSchema = z
  .object({ quantity: z.number().int().nonnegative(), confirmedPlans: confirmedPlansChoiceSchema.optional() })
  .strict()

export type SetRentalInput = z.infer<typeof setRentalInputSchema>

/** Update a phase's own fields (label, type, time range). */
export const updatePhaseInputSchema = z
  .object({
    label: phaseLabelSchema.optional(),
    type: phaseTypeSchema.optional(),
    startsAt: z.string().datetime().nullish(),
    endsAt: z.string().datetime().nullish(),
    /**
     * The setup instructions for this space, as markdown. Public — see
     * `docs/public-schedule.md`.
     *
     * Rides on this route rather than on the plan save: the plan save is the
     * canvas autosave, firing on every drag, and prose does not belong in it.
     * This route already refuses a frozen phase, which is exactly when
     * instructions stop being editable.
     *
     * Not trimmed to null when empty here — the service does that, so clearing
     * the box and clearing the field are the same act.
     */
    instructions: z.string().max(SETUP_INSTRUCTIONS_MAX).nullish(),
  })
  .strict()

export type UpdatePhaseInput = z.infer<typeof updatePhaseInputSchema>

/**
 * What to do with the client contacts on an event being deleted. `keep` leaves
 * their accounts alone (the default — deleting an event should not quietly delete
 * people); `remove` retires the ones this event was the last of, per
 * `clientRetirementFor`.
 */
/** Removing a brought-in piece: what happens to the confirmed plans that have it. */
export const removeEventFurnitureQuerySchema = z
  .object({ confirmedPlans: confirmedPlansChoiceSchema.default('keep') })
  .strict()

export type RemoveEventFurnitureQuery = z.infer<typeof removeEventFurnitureQuerySchema>

export const deleteEventQuerySchema = z
  .object({ clients: z.enum(['keep', 'remove']).default('keep') })
  .strict()

export type DeleteEventQuery = z.infer<typeof deleteEventQuerySchema>

/**
 * Canceling a booking: why, and optionally more.
 *
 * The reason is required and comes from a list, so losses are countable by
 * cause. The note is where the rest goes — and is the only place the detail of
 * an `other` lives, which is why that combination is worth a nudge rather than
 * a rule: a reason of `other` with no note records that something happened and
 * nothing about what.
 */
export const cancelEventInputSchema = z
  .object({
    reasonCode: staffCancellationReasonSchema,
    note: z.string().trim().max(2000).optional(),
  })
  .strict()

export type CancelEventInput = z.infer<typeof cancelEventInputSchema>

/**
 * Restoring a canceled booking. Nothing to say but why, and even that is
 * optional — the act is correcting a record, and the reason it was canceled is
 * already in the history.
 */
export const restoreEventInputSchema = z
  .object({
    note: z.string().trim().max(2000).optional(),
  })
  .strict()

export type RestoreEventInput = z.infer<typeof restoreEventInputSchema>

/**
 * Marking an inquiry lost: why, from the list every company shares, and
 * optionally more. As with canceling, a reason of `other` is where the note
 * matters. See `docs/funnel-stages.spec.md`.
 */
export const loseEventInputSchema = z
  .object({
    reason: lostReasonSchema,
    note: z.string().trim().max(2000).optional(),
  })
  .strict()

export type LoseEventInput = z.infer<typeof loseEventInputSchema>

/** Reopening a lost inquiry, because the couple came back. A note is optional. */
export const reopenEventInputSchema = z
  .object({
    note: z.string().trim().max(2000).optional(),
  })
  .strict()

export type ReopenEventInput = z.infer<typeof reopenEventInputSchema>

/** How many dates one inquiry can hold as requested. */
export const REQUESTED_DATES_MAX = 10

/**
 * The dates a couple asked about, saved whole. They hold nothing and block
 * nobody; one of them can be booked like any date. See
 * `docs/funnel-stages.spec.md`.
 */
export const requestedDatesInputSchema = z
  .object({
    dates: z
      .array(eventDaySchema)
      .max(REQUESTED_DATES_MAX, `An inquiry can hold up to ${String(REQUESTED_DATES_MAX)} requested dates.`)
      .refine((dates) => new Set(dates).size === dates.length, 'Each date can be requested once.'),
  })
  .strict()

export type RequestedDatesInput = z.infer<typeof requestedDatesInputSchema>

/**
 * Holding a date for an inquiry until `endsOn`. A hold ends on or before the day
 * it holds: holding a day past the day itself holds nothing. See
 * `docs/funnel-stages.spec.md`.
 */
export const holdInputSchema = z
  .object({
    date: eventDaySchema,
    endsOn: eventDaySchema,
  })
  .strict()
  .refine((hold) => hold.endsOn <= hold.date, {
    message: 'A hold ends on or before the day it holds.',
    path: ['endsOn'],
  })

export type HoldInput = z.infer<typeof holdInputSchema>
